'use client';

import { useEffect, type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { FilterSettingsFragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterSettingsFragmentFragmentDoc';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import { useMediaTabletType } from '@packages/shared/src/hooks/useMediaTabletType/useMediaTabletType';

import { useSearchFilters } from '../../hooks/useFilters';
import {
  type FilterSettingsMobileProps,
  FilterSettingsMobile,
} from '../FilterSettingsMobile/FilterSettingsMobile.client';
import { FilterSettingsDesktop } from '../FilterSettingsDesktop/FilterSettingsDesktop';
import { SelectedFilterValueList } from '../SelectedFilterValueList/SelectedFilterValueList';

/* GraphQL */ `
  fragment FilterSettingsFragment on SearchFilterableAndSortableResult {
    ...UseSearchFiltersFragment
    ...SelectedFilterValueListFragment
    ...FilterSettingsDesktopFragment
    ...FilterSettingsMobileFilterDialogButtonFragment
  }
`;

const messages = defineMessages({
  eraseFiltersNotification: {
    id: 'eraseFilters.notification',
    defaultMessage:
      'Die ausgewählten Filter der vorherigen Seite wurden entfernt, um Dir Artikel anbieten zu können. Nutze bei Bedarf erneut die Filterauswahl.',
  },
});

export type FilterSettingsProps = {
  maskedData: FragmentType<typeof FilterSettingsFragmentFragmentDoc>;
  showRemovedFiltersNotification?: boolean;
  renderCategoryDialogContent: FilterSettingsMobileProps['renderCategoryDialogContent'];
};

/**
 * Responsive component to handle all filter interactions
 * */
export const FilterSettings: FC<FilterSettingsProps> = ({
  maskedData,
  showRemovedFiltersNotification,
  renderCategoryDialogContent,
}) => {
  const isDesktop = useMediaType();
  const isTablet = useMediaTabletType();

  const data = unmask<typeof FilterSettingsFragmentFragmentDoc>(maskedData);

  const { selectedFilterValues, pushFilterChange } = useSearchFilters(data);
  const { formatMessage } = useIntl();
  const { addNotification } = useNotification();

  useEffect(() => {
    if (showRemovedFiltersNotification) {
      addNotification({
        message: formatMessage(messages.eraseFiltersNotification),
        severity: 'warning',
        duration: 7000,
      });
      pushFilterChange([]);
    }
  }, [addNotification, formatMessage, pushFilterChange, showRemovedFiltersNotification]);

  return (
    <>
      {isDesktop || isTablet ? (
        <FilterSettingsDesktop
          maskedData={data}
          selectedFilterValues={selectedFilterValues}
          onSubmit={pushFilterChange}
        />
      ) : (
        <FilterSettingsMobile
          maskedData={data}
          selectedFilterValues={selectedFilterValues}
          onSubmit={pushFilterChange}
          renderCategoryDialogContent={renderCategoryDialogContent}
        />
      )}
      <SelectedFilterValueList maskedData={data} showColors />
    </>
  );
};
